import React, { Fragment, useEffect, useState, useContext } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { UserMinusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../contexts/auth';
import { Link } from 'wouter';
import ComboboxAutocompleteAPI from './combobox_autocomplete_api';
import PrettyPagination from './pretty_pagination';
import { getEmployeesWithActiveContract } from '../services/api_calendar';

const InactiveEmployeesTable = ({ onClose }) => {
    const { token, profile } = useContext(AuthContext);
    const [filteredEmployees, setFilteredEmployees] = useState(null);
    const [totalResultNotActiveContract, setTotalResultNotActiveContract] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [pageNotActiveContract, setPageNotActiveContract] = useState(1);
    const [pageSizeNotActiveContract, setPageSizeNotActiveContract] = useState(5);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeesWithInactiveContract, setEmployeesWithInactiveContract] = useState([]);

    useEffect(() => {
        const fetchInactiveEmployees = async () => {
            setIsLoading(true);

            try {
                const response = await getEmployeesWithActiveContract(
                    token,
                    null,
                    'contract_not_active',
                    null,
                    pageNotActiveContract,
                    pageSizeNotActiveContract
                );

                setEmployeesWithInactiveContract(response.results || []);
                setTotalResultNotActiveContract(response.count || 0);

            } catch (error) {
                console.error('Errore nel recupero dei dipendenti inattivi:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchInactiveEmployees();
    }, [token, pageNotActiveContract, pageSizeNotActiveContract]);

    const handleEmployeeSelection = (employee) => {
        setSelectedEmployee(employee);
        if (employee) {
            setFilteredEmployees([employee]);
        } else {
            setFilteredEmployees(null);
        }
    };

    const handleResetSelection = () => {
        setSelectedEmployee(null);
        setFilteredEmployees(null);
    };

    const totalPages = Math.ceil(totalResultNotActiveContract / pageSizeNotActiveContract);

    const employeesToShow = filteredEmployees || employeesWithInactiveContract;

    return (
        <Transition.Root show={true} as={Fragment}>
            <Dialog as="div" className="relative z-[5000]" onClose={onClose}>
                {/* BACKDROP */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-start justify-center px-4 pb-6 pt-10 sm:px-6">

                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-2xl">
                                {/* HEADER MODALE */}
                                <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-indigo-200 px-5 py-4 sm:px-6">
                                    <div className="flex min-w-0 items-center gap-3">
                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
                                            <UserMinusIcon className="h-5 w-5" />
                                        </div>
                                        <div className="min-w-0">
                                            <Dialog.Title className="text-sm font-semibold text-indigo-800 uppercase">
                                                Dipendenti non attivi
                                            </Dialog.Title>

                                            <p className="mt-0.5 text-xs text-gray-600">
                                                Per riattivare un dipendente, seleziona il nominativo e aggiungi un nuovo contratto attivo
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex shrink-0 items-center gap-3">
                                        {totalResultNotActiveContract > 0 && (
                                            <div className="hidden rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-500 sm:block">
                                                <span className="font-semibold text-gray-800">
                                                    {totalResultNotActiveContract}
                                                </span>{' '}
                                                {totalResultNotActiveContract === 1 ? 'dipendente' : 'dipendenti'}
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="rounded-md p-1.5 text-gray-400 transition bg-gray-100 hover:bg-gray-50 hover:text-gray-700"
                                            aria-label="Chiudi"
                                        >
                                            <XMarkIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>

                                {/* CONTENUTO MODALE */}
                                <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 px-5 py-5 sm:px-6">
                                    {/* RICERCA */}
                                    <div className="mb-4 ">
                                        <div className="flex flex-col gap-3 md:flex-row md:items-end">
                                            <div className="min-w-0 flex-1">
                                                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                    Cerca dipendente
                                                </label>

                                                <div className="shadow-sm">
                                                    <ComboboxAutocompleteAPI
                                                        getData={async (query) => {
                                                            if (query.trim() === '') {
                                                                return Promise.resolve([]);
                                                            }

                                                            try {
                                                                const res =
                                                                    await getEmployeesWithActiveContract(
                                                                        token,
                                                                        query,
                                                                        'contract_not_active',
                                                                        null,
                                                                        1,
                                                                        5
                                                                    );
                                                                return res.results || [];
                                                            } catch (error) {
                                                                console.error('Errore durante il recupero dei dati:', error);
                                                                return [];
                                                            }
                                                        }}
                                                        getDisplay={(employee) =>
                                                            employee.last_name && employee.first_name
                                                                ? `${employee.last_name} ${employee.first_name}`
                                                                : employee.name || employee.cf || ''
                                                        }
                                                        getId={(employee) => employee.id}
                                                        onChange={handleEmployeeSelection}
                                                        currentValue={selectedEmployee}
                                                        placeholder="Cerca per nome, cognome o codice fiscale..."
                                                    />
                                                </div>
                                            </div>

                                            {selectedEmployee && (
                                                <button
                                                    type="button"
                                                    onClick={handleResetSelection}
                                                    className="inline-flex h-9 shrink-0 items-center justify-center rounded-md
                                                        border border-gray-300 bg-white px-3
                                                        text-[11px] font-semibold uppercase text-gray-600
                                                        shadow-sm transition-colors hover:bg-gray-50 hover:text-indigo-600
                                                    "
                                                >
                                                    Reset selezione
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* TABELLA */}
                                    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                                        {isLoading ? (
                                            <div className="flex min-h-48 items-center justify-center">
                                                <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                                                    <span className="h-2 w-2 animate-pulse rounded-full bg-indigo-500" />
                                                    Caricamento dipendenti...
                                                </div>
                                            </div>
                                        ) : employeesToShow.length === 0 ? (
                                            <div className="flex min-h-48 flex-col items-center justify-center px-5 text-center">
                                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                                                    <UserMinusIcon className="h-5 w-5 text-gray-400" />
                                                </div>
                                                <p className="mt-3 text-sm font-semibold text-gray-700">
                                                    Nessun dipendente non attivo
                                                </p>
                                                <p className="mt-1 text-xs text-gray-500">
                                                    Non sono presenti risultati da visualizzare.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="max-h-[45vh] overflow-auto">
                                                <table className="min-w-full border-collapse">
                                                    <thead>
                                                        <tr>
                                                            <th
                                                                className="
                                                                    sticky top-0 z-10 w-1/3
                                                                    bg-indigo-50 px-4 py-2.5 text-left text-[10px]
                                                                    font-semibold uppercase tracking-wide text-gray-500 ring-1 ring-inset ring-gray-200
                                                                "
                                                            >
                                                                Codice fiscale
                                                            </th>
                                                            <th
                                                                className="
                                                                    sticky top-0 z-10 w-1/3 bg-indigo-50
                                                                    px-4 py-2.5 text-left text-[10px] font-semibold uppercase
                                                                    tracking-wide text-gray-500 ring-1 ring-inset ring-gray-200
                                                                "
                                                            >
                                                                Cognome
                                                            </th>

                                                            <th
                                                                className="
                                                                    sticky top-0 z-10 w-1/3 bg-indigo-50 px-4 py-2.5
                                                                    text-left text-[10px] font-semibold uppercase tracking-wide
                                                                    text-gray-500 ring-1 ring-inset ring-gray-200
                                                                "
                                                            >
                                                                Nome
                                                            </th>
                                                        </tr>
                                                    </thead>

                                                    <tbody className="divide-y divide-gray-200 bg-white">
                                                        {[...employeesToShow]
                                                            .sort((a, b) => (a.last_name || a.cf).localeCompare(b.last_name || b.cf))
                                                            .map((employee) => (
                                                                <tr key={employee.id} className="cursor-pointer transition-colors hover:bg-indigo-50/60">

                                                                    <td className="w-1/3 px-4 py-3 text-xs font-semibold text-gray-600">
                                                                        <Link to={`/dipendenti/${employee?.cf}/edit?tab=2`} className="block">
                                                                            {employee.cf}
                                                                        </Link>
                                                                    </td>

                                                                    <td className="w-1/3 px-4 py-3 text-sm font-medium text-gray-700">
                                                                        <Link to={`/dipendenti/${employee?.cf}/edit?tab=2`} className="block">
                                                                            {employee.last_name || '-'}
                                                                        </Link>
                                                                    </td>

                                                                    <td className="w-1/3 px-4 py-3 text-sm font-normal text-gray-600">
                                                                        <Link to={`/dipendenti/${employee?.cf}/edit?tab=2`} className="block">
                                                                            {employee.first_name || '-'}
                                                                        </Link>
                                                                    </td>

                                                                </tr>
                                                            ))}
                                                    </tbody>

                                                </table>

                                            </div>
                                        )}
                                    </div>


                                    {/* PAGINAZIONE */}
                                    {filteredEmployees === null && (
                                        <PrettyPagination
                                            page={pageNotActiveContract}
                                            totalPages={totalPages}
                                            totalCount={totalResultNotActiveContract}
                                            onChange={(value) => setPageNotActiveContract(value)}
                                            labelSingolare="dipendente"
                                            labelPlurale="dipendenti"
                                            className="mt-5"
                                        />
                                    )}

                                </div>

                                {/* FOOTER MODALE */}
                                <div className="flex shrink-0 items-center justify-end border-t border-gray-200 bg-gray-50/70 px-5 py-3 sm:px-6">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="
                                            rounded-md border border-gray-300 bg-white px-5 py-2 text-xs
                                            font-semibold uppercase text-gray-700 shadow-sm transition-colors hover:bg-gray-50
                                        "
                                    >
                                        Chiudi
                                    </button>

                                </div>

                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default InactiveEmployeesTable;
